import {
    deleteJson,
    getJson,
    pathSegment,
    postJson,
    putEmpty,
    putJson,
    queryString,
} from "../client";
import type { SkipReason } from "./applications";
import { ENVOY_BASE } from "./base";

/** Where a saved posting stands, as the person last marked it. `envoy_api::saved::SavedStatus`. */
export type SavedStatus = "SAVED" | "APPLIED" | "SKIPPED";

/**
 * A posting the person saved to read later, where it stands and the lists
 * it is in. `envoy_api::saved::SavedPosting`. Marking it applied is the
 * person's own tracking, not an application.
 */
export interface SavedPosting {
    posting_id: string;
    saved_at: string;
    status: SavedStatus;
    /** When it was marked applied or skipped; null while only saved. */
    status_at: string | null;
    skip_reason: SkipReason | null;
    /** The lists it is in, the first it was put in first. */
    list_ids: string[];
}

/** One of the person's lists of saved postings. `envoy_api::saved::SavedList`. */
export interface SavedList {
    list_id: string;
    name: string;
    created_at: string;
    /** How many saved postings are in it. */
    count: number;
}

export interface SavedPostingList {
    saved: SavedPosting[];
    /** The oldest made first. */
    lists: SavedList[];
}

/** The most characters a list's name may have, once trimmed. */
export const MAX_LIST_NAME = 60;

/** Every posting the caller saved, the newest saved first. Saving costs nothing and tailors nothing. */
export function listSaved(): Promise<SavedPostingList> {
    return getJson(`${ENVOY_BASE}/saved`);
}

/** Saves a posting; saved before, it is answered as it was. A person keeps at most 500. */
export async function savePosting(postingId: string): Promise<SavedPosting> {
    return putEmpty(`${ENVOY_BASE}/saved/${pathSegment(postingId)}`);
}

/** Forgets a saved posting, and takes it out of every list. */
export async function unsavePosting(postingId: string): Promise<unknown> {
    return deleteJson(`${ENVOY_BASE}/saved/${pathSegment(postingId)}`);
}

/** Marks a saved posting applied or skipped, or back to only saved. A reason goes only with SKIPPED. */
export async function markSaved(
    postingId: string,
    status: SavedStatus,
    skipReason: SkipReason | null = null,
): Promise<SavedPosting> {
    return putJson(`${ENVOY_BASE}/saved/${pathSegment(postingId)}/status`, {
        status,
        ...(status === "SKIPPED" && skipReason ? { skip_reason: skipReason } : {}),
    });
}

/** Makes a list. A name the person already uses, whatever its case, answers that list. */
export function createSavedList(name: string): Promise<SavedList> {
    return postJson(`${ENVOY_BASE}/saved/lists`, { name });
}

/** Renames a list. Another list by that name is refused with a 409. */
export async function renameSavedList(listId: string, name: string): Promise<SavedList> {
    return putJson(`${ENVOY_BASE}/saved/lists/${pathSegment(listId)}`, { name });
}

/**
 * Deletes a list. Every posting in it stays saved, unless `unsave`: then
 * each one in no other list is unsaved too.
 */
export async function deleteSavedList(listId: string, unsave = false): Promise<unknown> {
    return deleteJson(
        `${ENVOY_BASE}/saved/lists/${pathSegment(listId)}${queryString({ unsave: unsave || null })}`,
    );
}

/** Puts a posting in a list, saving it if it was not. */
export async function addToSavedList(listId: string, postingId: string): Promise<SavedPosting> {
    return putEmpty(
        `${ENVOY_BASE}/saved/lists/${pathSegment(listId)}/postings/${pathSegment(postingId)}`,
    );
}

/** Takes a posting out of a list. It stays saved. */
export async function removeFromSavedList(listId: string, postingId: string): Promise<unknown> {
    return deleteJson(
        `${ENVOY_BASE}/saved/lists/${pathSegment(listId)}/postings/${pathSegment(postingId)}`,
    );
}
